/**
 * `/studio/clients/$slug?section=widgets` — what is embedded on this client's sites,
 * and the one control that had no screen.
 *
 * ## Why the tab exists
 *
 * `widgets_set_config!` has been an action since session 25 and its form was
 * deferred, in writing, to this session: `actionFields.ts` `HAND_WRITTEN` names "a
 * v1.1 Widgets section on the studio client page" as what draws it, and
 * `.plans/ai-product-placer/README.md` §6 item 12 puts it here. Until now a studio
 * member setting a widget's origins had to go over MCP or the studio RPC door.
 * `.docs/24` §The governing rule: "A capability the product promises must be
 * reachable from the interface."
 *
 * A widget belongs to a **client** — `widget.client_id`, `widgets_list`'s `client`
 * filter, and the client's own `/app/widgets/$ref` all say so — so the tab is beside
 * the other six rather than on a page of its own.
 *
 * ## The form is the widget's schema, not the action's
 *
 * `widgets_set_config`'s published input can only say that `config` is an object:
 * what is legal in it is the JSON Schema the widget snapshotted from its type when it
 * was created, which is a row in the database (`evidence/portal.md` §D.4). So this is
 * the one page that passes `ActionForm` a `schema` override — the snapshot, drawn one
 * field per property through the same `fieldOf` the client's own page uses — and
 * `under: 'config'` is what nests the drawn values back under the action's own field.
 *
 * **A widget whose type takes no configuration gets no form at all**, and says so in
 * one line rather than showing a button that opens an empty dialog. That is the same
 * `config_schema === null` the client's page reads to render nothing.
 *
 * **It replaces the whole configuration**, which is why every property is drawn and
 * why the current one is the form's defaults: the tool's own description says "this
 * replaces the whole configuration rather than merging into it", and a form that drew
 * half of it would blank the other half. The client's own form is the opposite — a
 * `PATCH` of the keys that moved — because it is only ever shown a part.
 *
 * ## What each widget costs this client (session 46d)
 *
 * Every row states its price in the words every page uses (`format/widgetPrice.ts`), and
 * where the studio has agreed a price of the client's own it says so — a *Custom price*
 * tag, the rate card's price beside it, and the reason and date the price was set, all
 * read from `widgets_list_client_prices` rather than worked out here. The two dialogs are
 * the two tools, fixed to the client and the kind: a client price is per **kind**, so
 * setting one on a row re-prices every widget of that kind the client has, and the
 * dialog says so before anybody presses it.
 */

import type { ClientWidgetPriceView, StudioClientDetailView, WidgetView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'

import { useStudioRead } from '../../../api/studio.js'
import { Button } from '../../../components/portal/Button.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Push, Row, Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Tag } from '../../../components/portal/Status.js'
import { B, Muted, Num, Ref, Small } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { moneyFieldValue } from '../../../components/studio/ActionForm.js'
import { useFormat } from '../../../format/useFormat.js'
import { widgetPriceWords } from '../../../format/widgetPrice.js'
import { hostedWorkspaceOf } from '../../../shell/hostedSurfaces.js'

export const WIDGETS_ID = 'widgets'

export type WidgetsSectionProps = {
  readonly slug: string
  /** The page's own read, for the client's name in the empty line. */
  readonly detail: StudioClientDetailView
}

export function WidgetsSection({ slug, detail }: WidgetsSectionProps) {
  const format = useFormat()
  /*
   * `inline: true`, for `FilesSection`'s reason: a list that failed should be one
   * line in its own section rather than the whole client detail replaced by the
   * route boundary's error.
   */
  const list = useStudioRead('widgets_list', { client: slug }, { inline: true })
  const widgets = list.data?.widgets ?? []
  // The client's own prices beside the card's — one read for every row (session 46d).
  const prices = useStudioRead('widgets_list_client_prices', { client: slug }, { inline: true })
  const priceOf = (code: string) => prices.data?.items.find((item) => item.widget_type === code)

  return (
    <Section id={WIDGETS_ID}>
      <SectionHead
        title="Widgets"
        note={format.qty(widgets.length, { one: 'widget', other: 'widgets' })}
        pushNote
      />

      <Card>
        {list.isPending ? (
          <CardBody>
            <Empty>Loading this client’s widgets…</Empty>
          </CardBody>
        ) : widgets.length === 0 ? (
          <CardBody>
            <Empty>{detail.account.name} has no widgets embedded anywhere.</Empty>
          </CardBody>
        ) : (
          widgets.map((widget) => (
            <CardBody key={widget.ref}>
              <WidgetRow widget={widget} price={priceOf(widget.widget_type)} />
            </CardBody>
          ))
        )}
      </Card>
    </Section>
  )
}

function WidgetRow({
  widget,
  price,
}: {
  readonly widget: WidgetView
  /** This kind on the client's price list, once it has been read. */
  readonly price: ClientWidgetPriceView | undefined
}) {
  const format = useFormat()
  const workspace = hostedWorkspaceOf(widget.hosted_by)
  // Null for a surface the studio works over MCP and has no page for — a forms widget.
  const studioHref = workspace?.studioHref ?? null
  const schema = widget.config_schema

  return (
    <Stack gap="tight">
      <Row>
        <B>{widget.name}</B>
        <Tag>{widget.site}</Tag>
        <Push />
        {/*
          Session 46: a widget the portal hosts is worked in its workspace — a blog's posts,
          written for the client here as they would be in the client's own portal.
        */}
        {workspace === null || studioHref === null ? null : (
          <Button size="sm" asChild>
            <Link to={studioHref(widget.client, widget.ref)}>{workspace.open}</Link>
          </Button>
        )}
        {/*
          Session 46: a widget the portal hosts has a Connect page — its keys, its
          endpoints, its site hooks — mounted here as well as in the client's portal,
          because the studio sets a site up for a client who may have no login.
        */}
        {widget.hosted_by === null ? null : (
          <Button size="sm" asChild>
            <Link
              to="/studio/clients/$slug/connect/$widgetRef"
              params={{ slug: widget.client, widgetRef: widget.ref }}
            >
              Connect the site
            </Link>
          </Button>
        )}
        {schema === null ? null : (
          <ActionDialog
            action="widgets_set_config"
            schema={{ node: schema, under: 'config' }}
            label="Set the configuration"
            title={`${widget.name} — configuration`}
            submitLabel="Save the configuration"
            size="sm"
            fixed={{ widget: widget.ref }}
            /*
             * The whole of the current configuration, because the action replaces the
             * whole of it. `ActionForm` puts each value into the shape its control
             * holds — a number as text, a list of origins as one line each — through
             * `components/portal/schemaForm.ts` `asFormValue`.
             */
            defaults={widget.config ?? {}}
          >
            <Small>
              <Muted>
                This replaces the whole configuration and keeps the old one: the version goes up by
                one and a generation recorded under version {format.number(widget.config_version)}{' '}
                still resolves.
              </Muted>
            </Small>
          </ActionDialog>
        )}
      </Row>

      <WidgetPrice widget={widget} price={price} />

      <Small>
        <Muted>
          <Ref value={widget.ref} />
          {' · '}
          {schema === null ? (
            'This kind of widget takes no configuration.'
          ) : widget.config_version === 0 ? (
            'Not configured yet.'
          ) : (
            <>
              Configuration version <Num value={widget.config_version} />
            </>
          )}
        </Muted>
      </Small>
    </Stack>
  )
}

/**
 * What the widget costs this client, whose price it is, and the two ways to change it
 * (session 46d). The words are `widgetPriceWords`' — the same phrase the client's own
 * widget page prints — and the card's price and the reason come off the client's price
 * list, so nothing here compares two prices to decide which one is custom.
 */
function WidgetPrice({
  widget,
  price,
}: {
  readonly widget: WidgetView
  readonly price: ClientWidgetPriceView | undefined
}) {
  const format = useFormat()
  const custom = widget.price_source === 'client'
  const kind = price?.widget_type_name ?? widget.widget_type
  const latest = custom ? price?.history[0] : undefined

  return (
    <Stack gap="tight">
      <Row>
        <Small>{widgetPriceWords(widget, format)}</Small>
        {custom ? <Tag>Custom price</Tag> : null}
        <Push />
        <ActionDialog
          action="widgets_set_client_price"
          label="Set this client’s price"
          title={`${kind} — this client’s price`}
          submitLabel="Set the price"
          size="sm"
          fixed={{ client: widget.client, widget_type: widget.widget_type }}
          defaults={{
            // The box holds major units and its currency; the wire holds minor units.
            unit_amount: moneyFieldValue(widget.unit_amount),
            monthly_amount: moneyFieldValue(widget.monthly_amount),
            included_qty: widget.included_qty,
            monthly_cap: widget.monthly_cap,
          }}
        >
          <Small>
            <Muted>
              In this client’s currency, billed exactly as typed. It re-prices every {kind} widget
              this client has for the whole of this month, which has not been billed yet, and a new
              one starts at it. A month already invoiced is never touched.
            </Muted>
          </Small>
        </ActionDialog>
        {custom ? (
          <ActionDialog
            action="widgets_remove_client_price"
            label="Back to the card"
            title={`${kind} — back to the rate card`}
            submitLabel="Back to the card"
            size="sm"
            fixed={{ client: widget.client, widget_type: widget.widget_type }}
          >
            <Small>
              <Muted>
                Every {kind} widget this client has goes back to today’s card
                {price === undefined
                  ? ''
                  : ` — ${widgetPriceWords({ ...price.card, unit_noun: widget.unit_noun }, format)}`}
                , from this month. The price it ends stays in the history.
              </Muted>
            </Small>
          </ActionDialog>
        ) : null}
      </Row>
      {custom && price !== undefined ? (
        <Small>
          <Muted>
            The card would be{' '}
            {widgetPriceWords({ ...price.card, unit_noun: widget.unit_noun }, format)}.
            {/* The name may end in a full stop of its own ("Ferit M."), so the date ends
                the sentence and the studio's own words follow it as they were written. */}
            {latest === undefined
              ? null
              : ` Set ${latest.set_by === null ? '' : `by ${latest.set_by} `}on ${format.date(latest.set_at)} — ${latest.reason}`}
          </Muted>
        </Small>
      ) : null}
    </Stack>
  )
}
